import config from '../config';
import http from '../utils/http';
import { DEFAULT_GREETING_IMAGE } from '../mapping/defaults';
import { IApplicationState } from '../reducers';
import actionTypes from '../actions/actionTypes';
import { saveAs } from 'file-saver';
import {
    QUOTA_STATUS,
    PROJECT_STATUS,
    Project,
    ProjectInfo,
    VIEW_MODE
} from '@nexxt/common/types';
import { getPrice } from './payment.service';
import { getTenantLevel } from '@nexxt/common/firebase/auth/userDetails';
const { api } = config;
import { BUILDER_STATES } from '../interface/common.interface';
import { getSortedQuestionByProjectId } from './question.service';

/**
 * Get projects.
 *
 * @return {Object}
 */
export function fetchProjects() {
    return async (dispatch, getState: () => IApplicationState) => {
        try {
            const url = `${api.endpoints.project}`;
            const tenantLevel = getState().authReducer.tenant.tenantLevel;
            const response = await http.get(url);
            dispatch({
                type: actionTypes.GET_PROJECTS,
                payload: {
                    ...response.data,
                    status,
                    tenantLevel
                }
            });
            return response;
        } catch (e) {
            throw e;
        }
    };
}

/**
 * Get filtered projects.
 *
 * @return {Object}
 */
export async function fetchFilteredProjects(id, filter = '') {
    const url = `${api.endpoints.project}?tenantID=${id}${filter}`;
    const response = await http.get(url);

    return response;
}

/**
 * Get filtered projects.
 *
 * @return {Object}
 */
export async function sortProject(id, sort = '') {
    const url = `${api.endpoints.project}?tenantID=${id}${sort}`; //TODOv2: ?tenantID=${id}
    const response = await http.get(url);

    return response;
}

/**
 * Create new projects.
 *
 * @return {Object}
 */
export async function createProject(data) {
    const url = `${api.endpoints.project}`; //TODOv2: ?tenantID=${id}
    const response = await http.post(url, {
        ...data,
        status: PROJECT_STATUS.DRAFT,
        greetingImage: DEFAULT_GREETING_IMAGE
    }); // @TODO: setting default value

    return response;
}

/**
 * Update project.
 *
 * @return {Object}
 */
export function updateProject(projectID, data) {
    return async (dispatch, getState: () => IApplicationState) => {
        const url = `${api.endpoints.project}/${projectID}`;
        const response = await http.put(url, data);

        await dispatch(getPrice({ projectID }));

        return response;
    };
}

/**
 * Fetch project by id.
 *
 * @return {Object}
 */
export async function fetchProject(id) {
    const url = `${api.endpoints.project}/${id}`; //TODOv2: ?tenantID=${id} in backend
    const response = await http.get(url);

    return response;
}

/**
 * Archive project.
 *
 * @return {Object}
 */
export async function archiveProject(projectId) {
    const url = `${api.endpoints.project}/${projectId}`; //TODOv2 in /tenants/${tenantId}

    const response = await http.delete(url);

    return response;
}

/**
 * Duplicate project.
 *
 * @return {Object}
 */
export async function duplicateProject(projectId, data) {
    const url = `${api.endpoints.project}/${projectId}/duplicate`; //TODOv2 in /tenants/${tenantId}

    const response = await http.post(url, data);

    return response;
}

export async function getPackagedSolution(projectId, tenantID, name) {
    const url = `${api.endpoints.project}/packagedSolution/${projectId}`;

    const response = await http.post(url, {
        tenantID,
        name
    });

    return response;
}

export async function createSetupWizardProject(formData, templateSettings) {
    try {
        const createRequest = await getPackagedSolution(
            templateSettings.projectID,
            templateSettings.tenantID,
            formData.name
        );
        const projectId = createRequest.data.data;
        const enableEmail = process.env.NODE_ENV == 'production';
        const url = `${api.endpoints.project}/wizard`;
        const response = await http.post(url, {
            projectId,
            formData,
            templateSettings,
            enableEmail
        });

        return response?.data;
    } catch (err) {
        return null;
    }
}

export async function downloadCSV(projectID, projectTitle, tenantID, type) {
    const url = `${api.endpoints.project}/${projectID}/export?mode=${type}&csv`;
    const fileName = `${projectTitle}_${new Date().toJSON()}.xlsx`;

    const response = await http
        .get(url, {
            responseType: 'blob'
        })
        .then((x) => {
            // const contentDisposition = x.headers['content-disposition'];
            // if (contentDisposition) {
            //     fileName = contentDisposition.split('filename=')[1];
            // }
            return saveAs(new Blob([x.data]), fileName);
        })
        .catch((err) => err.response.status);

    return { response, fileName };
}

export async function importCSV(projectID, file, languageChoice) {
    try {
        const url = `${api.endpoints.project}/${projectID}/import?mode=translation&process=1`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('languageChoice', JSON.stringify(languageChoice));

        const response = await http.post(url, formData);
        return response.data;
    } catch (err) {
        return err.message;
    }
}

export async function preprocessingTranslation(
    projectId: number,
    file,
    languageChoice
) {
    try {
        const url = `${api.endpoints.project}/${projectId}/import?mode=translation&process=0`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('languageChoice', JSON.stringify(languageChoice));
        const response = await http.post(url, formData);
        return response.data;
    } catch (err) {
        console.error(err);
        return [];
    }
}
export const clearProjects = () => ({
    type: actionTypes.CLEAR_PROJECT
});

/**
 * Preview the project
 * @param projectId
 */
export function previewProject(projectId: number) {
    return async (dispatch, getState: () => IApplicationState) => {
        let url = `${api.endpoints.project}/${projectId}/preview/`;
        try {
            const response = await http.post(url, { projectId });
            dispatch({
                type: actionTypes.PREVIEW_PROJECT,
                payload: {
                    projectPreviewPackageId: response.data.packageID
                }
            });
            return response;
        } catch (e) {
            throw e;
        }
    };
}

/**
 * Launch the project
 * @param projectId
 */
export function onLaunch(
    projectId: number,
    packageID: number,
    codeName: string,
    enableEmail = false,
    disablePanel = true, // for re-publish
    orderReview = null,
    complete = false
) {
    return async (dispatch, getState: () => IApplicationState) => {
        const queryParams = disablePanel ? '?disablePanel' : '';
        const url = `${api.endpoints.project}/${projectId}/publish${queryParams}`;
        try {
            const response = await http.post(url, {
                packageID,
                codeName,
                enableEmail,
                orderReview,
                complete
            });
            dispatch({
                type: actionTypes.SET_PROJECT,
                payload: {
                    ...response.data.updatedProjectData,
                    packageID: response.data.packageID
                }
            });
            return response;
        } catch (e) {
            throw e;
        }
    };
}

/**
 * Converts a Project to its corresponding ProjectInfo
 * @param project
 */
export const projectToProjectInfo = (project: Project): ProjectInfo => {
    return {
        id: project.id,
        avatarImage: project.avatarImage,
        pageTitle: project.title,
        botSpeed: project.botSpeed,
        sessionTimeout: project.sessionTimeout,
        showProgress: project.showProgress,
        redirectURLs: {
            [QUOTA_STATUS.COMPLETE]: project.redirectURL,
            [QUOTA_STATUS.TERMINATE]: project.redirectURLTermination,
            [QUOTA_STATUS.QUOTA_FULL]: project.redirectURLQuotafull
        },
        botTheme: project.botTheme,
        name: project.name,
        faviconImage: project.faviconImage,
        devices: project.devices,
        templateCode: project?.templateCode,
        predefinedAudience: project?.predefinedAudience,
        discoursePolicy: project?.discoursePolicy,
        redirectSettings: project?.redirectSettings,
        panelSettings: project?.panelSettings
    };
};

export function launchSandbox(projectID: number) {
    const endpoint = `${api.endpoints.project}/${projectID}/status/review`;
    const email = process.env.NODE_ENV == 'production';

    return async (dispatch, getState: () => IApplicationState) => {
        try {
            http.post(endpoint, {
                email
            });
            dispatch({
                type: actionTypes.SET_PROJECT_STATUS,
                payload: { status: PROJECT_STATUS.REVIEW }
            });
            dispatch(previewProject(projectID));
        } catch (err) {}
    };
}
/**
 * Update project status
 * @param projectID
 * @param status
 */
export function onPause(projectID: number, status: PROJECT_STATUS) {
    let url = `${api.endpoints.project}/${projectID}/status/pause`;
    return async (dispatch, getState: () => IApplicationState) => {
        const project = {
            ...getState().projectReducer.currentProject,
            status
        };
        try {
            const response = await http.post(url, project);
            dispatch({
                type: actionTypes.SET_PROJECT_STATUS,
                payload: {
                    ...response.data,
                    status
                }
            });
            return response;
        } catch (e) {
            throw e;
        }
    };
}

export function checkProjectHealth(projectID: number, ignoreQids = []) {
    return async (dispatch, getState: () => IApplicationState) => {
        try {
            const { currentQuestionId, builderState, showError } =
                getState().questionReducer;

            if (
                !showError &&
                builderState == BUILDER_STATES.EDIT &&
                currentQuestionId
            ) {
                ignoreQids.push(currentQuestionId);
            }

            let url = `${api.endpoints.project}/${projectID}/health`;
            if (ignoreQids.length) {
                url += `?ignoreIds=${ignoreQids.join(',')}`;
            }
            const response = await http.get(url);
            const { problems, refreshQuestionSummary } = response.data;
            dispatch({
                type: actionTypes.CHECK_PROJECT_HEALTH,
                payload: problems
            });

            if (refreshQuestionSummary) {
                await dispatch(getSortedQuestionByProjectId(projectID));
            }
            return response;
        } catch (e) {
            console.error(e);
            return null;
        }
    };
}

/*
 * Fetches the latest lock user for projectID from server, and updates state accordingly with SET_LOCK_USER.
 */
export function applyProjectLock(projectID: number) {
    const url = `${api.endpoints.project}/${projectID}/lock`;
    return async (dispatch, getState: () => IApplicationState) => {
        try {
            const currentLockUser = getState().projectReducer?.currentLockUser;
            const resp = await http.get(url);
            if (currentLockUser?.userId != resp.data?.userId)
                dispatch({
                    type: actionTypes.SET_LOCK_USER,
                    payload: resp.data
                });
            return resp;
        } catch (e) {
            throw e;
        }
    };
}

/*
 * Queries lock endpoint to set user id (in data) as lock user for projectID.
 */
export function updateProjectLockUser(projectID: number, data) {
    const url = `${api.endpoints.project}/${projectID}/lock`;
    return async (dispatch, getState: () => IApplicationState) => {
        try {
            const resp = await http.post(url, data);
            return resp;
        } catch (err) {
            throw err;
        }
    };
}
